import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../axios';
import { toast } from 'react-toastify';
import {
  Container,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Box,
  TextField,
  Button,
  MenuItem,
  Grid,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  InputAdornment,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  Collapse,
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Print as PrintIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AccountBalance as AccountBalanceIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import VoucherPrintDialog from './VoucherPrintDialog';
import { useAuth } from "../../context/AuthContext";
import { PERMISSIONS } from "../../constants/permissions";

const VouchersListPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { hasPermission } = useAuth();
  const companyId = parseInt(localStorage.getItem('companyId'), 10);
  
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [selectedVoucherForPrint, setSelectedVoucherForPrint] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(true);
  
  const [filters, setFilters] = useState({
    companyId,
    voucherType: '',
    paymentType: '',
    customerId: '',
    supplierId: '',
    fromDate: dayjs().startOf('month').format('YYYY-MM-DD'),
    toDate: dayjs().endOf('month').format('YYYY-MM-DD'),
    minAmount: '',
    maxAmount: '',
    searchText: ''
  });

  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  
  const voucherTypes = [
    { value: '', label: 'جميع الأنواع' },
    { value: 101, label: 'سند قبض من عميل' },
    { value: 102, label: 'سند صرف لمورد' },
    { value: 103, label: 'سند قبض عام' },
    { value: 104, label: 'سند صرف عام' },
    { value: 105, label: 'سند صرف ضريبة' }
  ];
  
  const paymentTypes = [
    { value: '', label: 'جميع طرق الدفع' },
    { value: 1, label: 'نقدي' },
    { value: 2, label: 'بنكي' }
  ];

  useEffect(() => {
    fetchVouchers();
    fetchSummary();
    fetchCustomers();
    fetchSuppliers();
    loadCompanyInfo();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchVouchers();
      fetchSummary();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [filters]);

  const fetchVouchers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          if (['voucherType', 'paymentType', 'customerId', 'supplierId', 'minAmount', 'maxAmount'].includes(key)) {
            params.append(key, Number(value));
          } else {
            params.append(key, value);
          }
        }
      });

      const response = await axios.get(`/Vouchers?${params.toString()}`);
      const vouchersData = response.data?.data || response.data?.vouchers || response.data || [];

      if (!Array.isArray(vouchersData)) {
        console.warn('⚠️ البيانات المستلمة ليست مصفوفة:', response.data);
        setVouchers([]);
        return;
      }

      const processedVouchers = vouchersData.map((voucher, index) => ({
        ...voucher,
        id: voucher.id || `temp-${index}`,
        voucherNumber: voucher.voucherNumber || 'غير محدد',
        voucherTypeName: voucher.voucherTypeName || 'غير محدد',
        amount: Number(voucher.amount) || 0
      }));

      setVouchers(processedVouchers);
    } catch (error) {
      console.error('خطأ في جلب السندات:', error);
      toast.error(`فشل في جلب السندات: ${error.response?.data?.message || error.message}`);
      setVouchers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const params = new URLSearchParams({
        companyId,
        fromDate: filters.fromDate,
        toDate: filters.toDate
      });
      const response = await axios.get(`/Vouchers/summary?${params.toString()}`);
      setSummary(response.data);
    } catch (error) {
      console.error('خطأ في جلب ملخص السندات:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`/Customer/by-company/${companyId}`);
      setCustomers(response.data || []);
    } catch (error) {
      console.error('خطأ في جلب العملاء:', error);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`/Supplier/by-company/${companyId}`);
      setSuppliers(response.data || []);
    } catch (error) {
      console.error('خطأ في جلب الموردين:', error);
    }
  };

  const loadCompanyInfo = async () => {
    try {
      const response = await axios.get(`/CompanyProfile/public/${companyId}`);
      setCompanyInfo({
        name: response.data.nameAr,
        nameEn: response.data.nameEn,
        taxNumber: response.data.vatNo,
        address: response.data.address,
        phone: response.data.phone,
        email: response.data.email,
        logoUrl: response.data.logoUrl
      });
    } catch (error) {
      console.error('خطأ في تحميل معلومات الشركة:', error);
    }
  };

  const handleSearch = () => {
    fetchVouchers();
    fetchSummary();
  };

  const handleReset = () => {
    setFilters({
      companyId,
      voucherType: '',
      paymentType: '',
      customerId: '',
      supplierId: '',
      fromDate: dayjs().startOf('month').format('YYYY-MM-DD'),
      toDate: dayjs().endOf('month').format('YYYY-MM-DD'),
      minAmount: '',
      maxAmount: '',
      searchText: ''
    });
  };

  const handleFilterChange = (field, value) => {
    if (field === 'voucherType' && value !== '') {
      setFilters(prev => ({ ...prev, [field]: Number(value) }));
    } else {
      setFilters(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleDeleteVoucher = async (voucherId) => {
    try {
      await axios.delete(`/Vouchers/${voucherId}`);
      toast.success('تم حذف السند بنجاح');
      fetchVouchers();
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error('خطأ في حذف السند:', error);
      toast.error('فشل في حذف السند');
    }
  };

  const handlePrintVoucher = (voucher) => {
    setSelectedVoucherForPrint(voucher);
    setPrintDialogOpen(true);
  };

  const handleViewVoucher = (voucher) => {
    switch (voucher.voucherType) {
      case 101: // Customer Receipt
        navigate(`/vouchers/customer-receipt/${voucher.id}/view`);
        break;
      case 102: // Supplier Payment
        navigate(`/vouchers/supplier-payment/${voucher.id}/view`);
        break;
      case 103: // General Receipt
        navigate(`/vouchers/general-receipt/${voucher.id}/view`);
        break;
      case 104: // General Payment
        navigate(`/vouchers/general-payment/${voucher.id}/view`);
        break;
      case 105: // Tax Payment
        navigate(`/vouchers/tax-payment/${voucher.id}/view`);
        break;
      default:
        toast.error('نوع السند غير معروف');
    }
  };

  const handleEditVoucher = (voucher) => {
    switch (voucher.voucherType) {
      case 101: // Customer Receipt
        navigate(`/vouchers/customer-receipt/${voucher.id}/edit`);
        break;
      case 102: // Supplier Payment
        navigate(`/vouchers/supplier-payment/${voucher.id}/edit`);
        break;
      case 103: // General Receipt
        navigate(`/vouchers/general-receipt/${voucher.id}/edit`);
        break;
      case 104: // General Payment
        navigate(`/vouchers/general-payment/${voucher.id}/edit`);
        break;
      case 105: // Tax Payment
        navigate(`/vouchers/tax-payment/${voucher.id}/edit`);
        break;
      default:
        toast.error('نوع السند غير معروف');
    }
  };

  const columns = [
    {
      field: 'voucherNumber',
      headerName: 'رقم السند',
      width: 120,
      renderCell: (params) => {
        if (!params || !params.value) return <span>-</span>;
        return (
          <Chip 
            label={params.value} 
            size="small" 
            color="primary" 
            variant="outlined"
          />
        );
      }
    },
    {
      field: 'voucherTypeName',
      headerName: 'نوع السند',
      width: 150,
      renderCell: (params) => {
        if (!params || !params.row) return <span>-</span>;
        const type = params.row.voucherType;
        const typeConfig = {
          101: { color: 'success', icon: <ReceiptIcon /> },
          102: { color: 'error', icon: <PaymentIcon /> },
          103: { color: 'primary', icon: <ReceiptIcon /> },
          104: { color: 'warning', icon: <PaymentIcon /> },
          105: { color: 'warning', icon: <PaymentIcon /> },
        };
        const config = typeConfig[type] || { color: 'default', icon: null };
        return (
          <Chip 
            label={params.value || 'غير محدد'}
            size="small"
            color={config.color}
            icon={config.icon}
          />
        );
      }
    },
    {
      field: 'voucherDate',
      headerName: 'التاريخ',
      width: 140,
      renderCell: (params) => {
        if (!params || !params.value) return <span>-</span>;
        try {
          const date = dayjs(params.value);
          if (!date.isValid()) return <span>-</span>;
          return (
            <Tooltip title={date.format('YYYY/MM/DD HH:mm:ss')}>
              <Chip
                label={date.format('YYYY/MM/DD')}
                size="small"
                variant="outlined"
                color="default"
              />
            </Tooltip>
          );
        } catch {
          return <span>-</span>;
        }
      }
    },
    {
      field: 'customerName',
      headerName: 'العميل/المورد',
      width: 180,
      renderCell: (params) => {
        if (!params || !params.row) return <span>عام</span>;
        const name = params.row.customerName || params.row.supplierName;
        if (!name) return <span style={{ color: '#666' }}>عام</span>;
        const isCustomer = !!params.row.customerName;
        return (
          <Chip
            label={name}
            size="small"
            color={isCustomer ? 'info' : 'warning'}
            variant="outlined"
          />
        );
      }
    },
    {
      field: 'paymentTypeName',
      headerName: 'طريقة الدفع',
      width: 120,
      renderCell: (params) => {
        if (!params || !params.row) return <span>غير محدد</span>;
        const paymentType = params.row.paymentType;
        const paymentTypeName = params.row.paymentTypeName || 
          (paymentType === 1 ? 'نقدي' : paymentType === 2 ? 'بنكي' : 'غير محدد');
        return (
          <Chip
            label={paymentTypeName}
            size="small"
            color={paymentType === 1 ? 'success' : paymentType === 2 ? 'primary' : 'default'}
            variant="outlined"
          />
        );
      }
    },
    {
      field: 'amount',
      headerName: 'المبلغ',
      width: 140,
      type: 'number',
      renderCell: (params) => {
        const amount = params?.value || 0;
        const isReceipt = params?.row?.voucherType === 101 || params?.row?.voucherType === 103;
        return (
          <Chip
            label={new Intl.NumberFormat('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }).format(amount) + ' ريال'}
            size="small"
            color={isReceipt ? 'success' : 'error'}
            variant="outlined"
          />
        );
      }
    },
    {
      field: 'branchName',
      headerName: 'الفرع',
      width: 140,
      renderCell: (params) => {
        const name = params?.value || '-';
        return <Chip label={name} size="small" variant="outlined" />;
      }
    },
    {
      field: 'description',
      headerName: 'الوصف',
      width: 200,
      renderCell: (params) => {
        const value = params?.value || params?.row?.notes || '';
        return (
          <Tooltip title={value}>
            <span>{value || '-'}</span>
          </Tooltip>
        );
      }
    },
    {
      field: 'actions',
      headerName: 'الإجراءات',
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        if (!params || !params.row) return null;
        const type = params.row.voucherType;
        const isReceipt = type === 101 || type === 103;
        const isPayment = type === 102 || type === 104 || type === 105;
        
        const canView = isReceipt 
          ? hasPermission(PERMISSIONS.BOND_RECEIPT_VIEW) 
          : (isPayment ? hasPermission(PERMISSIONS.BOND_PAYMENT_VIEW) : hasPermission(PERMISSIONS.VOUCHER_VIEW));
        const canEdit = isReceipt 
          ? hasPermission(PERMISSIONS.BOND_RECEIPT_EDIT) 
          : (isPayment ? hasPermission(PERMISSIONS.BOND_PAYMENT_EDIT) : false);
        const canPrint = isReceipt 
          ? hasPermission(PERMISSIONS.BOND_RECEIPT_PRINT) 
          : (isPayment ? hasPermission(PERMISSIONS.BOND_PAYMENT_PRINT) : false);
        const canDelete = isReceipt 
          ? hasPermission(PERMISSIONS.BOND_RECEIPT_DELETE) 
          : (isPayment ? hasPermission(PERMISSIONS.BOND_PAYMENT_DELETE) : false);

        return (
          <Stack direction="row" spacing={1}>
             {/* ✅ View Journal Entry Button */}
            {params.row.journalEntryGlobalId && (
              <Tooltip title="عرض القيد">
                <IconButton 
                  size="small" 
                  sx={{ color: '#9c27b0' }} 
                  onClick={() => window.open(`/journal-entries/${params.row.journalEntryGlobalId}/view`, "_blank")}
                >
                  <ViewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {canView && (
              <Tooltip title="عرض">
                <IconButton size="small" color="info" onClick={() => handleViewVoucher(params.row)}>
                  <ViewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canEdit && (
              <Tooltip title="تعديل">
                <IconButton size="small" color="primary" onClick={() => handleEditVoucher(params.row)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canPrint && (
              <Tooltip title="طباعة">
                <IconButton size="small" color="secondary" onClick={() => handlePrintVoucher(params.row)}>
                  <PrintIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title="حذف">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => {
                    setSelectedVoucher(params.row);
                    setDeleteDialogOpen(true);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        );
      }
    }
  ];

  return (
    <Container
      maxWidth={false}
      sx={{
        mt: { xs: 2, sm: 3, md: 4 },
        px: { xs: 2, sm: 3, md: 4 },
        pb: { xs: 3, sm: 4, md: 5 }
      }}
      dir="rtl"
    >
      {/* Header */}
      <Paper
        elevation={3}
        sx={{
          background: 'linear-gradient(135deg, #00897b 0%, #00695c 100%)',
          borderRadius: { xs: 2, sm: 2.5, md: 3 },
          mb: { xs: 3, sm: 4 },
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: { xs: 2, sm: 3, md: 3.5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <AccountBalanceIcon sx={{ fontSize: { xs: 32, sm: 40, md: 48 }, color: 'white' }} />
              <Typography
                variant={isMobile ? "h5" : "h4"}
                fontWeight="bold"
                color="white"
                sx={{
                  fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                  textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                }}
              >
                💰 إدارة السندات المالية
              </Typography>
            </Box>
            <Stack direction={isMobile ? "column" : "row"} spacing={1} sx={{ width: isMobile ? '100%' : 'auto' }}>
              {hasPermission(PERMISSIONS.BOND_RECEIPT_CREATE) && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/vouchers/customer-receipt')}
                  sx={{
                    bgcolor: 'success.main',
                    '&:hover': { bgcolor: 'success.dark' },
                    borderRadius: 2,
                    boxShadow: 3
                  }}
                >
                  سند قبض عميل
                </Button>
              )}
              {hasPermission(PERMISSIONS.BOND_PAYMENT_CREATE) && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/vouchers/supplier-payment')}
                  sx={{
                    bgcolor: 'error.main',
                    '&:hover': { bgcolor: 'error.dark' },
                    borderRadius: 2,
                    boxShadow: 3
                  }}
                >
                  سند صرف مورد
                </Button>
              )}
              {hasPermission(PERMISSIONS.BOND_RECEIPT_CREATE) && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/vouchers/general-receipt')}
                  sx={{
                    bgcolor: 'info.main',
                    '&:hover': { bgcolor: 'info.dark' },
                    borderRadius: 2,
                    boxShadow: 3
                  }}
                >
                  سند قبض عام
                </Button>
              )}
              {hasPermission(PERMISSIONS.BOND_PAYMENT_CREATE) && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/vouchers/general-payment')}
                  sx={{
                    bgcolor: 'secondary.main',
                    '&:hover': { bgcolor: 'secondary.dark' },
                    borderRadius: 2,
                    boxShadow: 3
                  }}
                >
                  سند صرف عام
                </Button>
              )}
              {hasPermission(PERMISSIONS.BOND_TAX_CREATE) && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/vouchers/tax-payment')}
                  sx={{
                    bgcolor: 'warning.main',
                    '&:hover': { bgcolor: 'warning.dark' },
                    borderRadius: 2,
                    boxShadow: 3
                  }}
                >
                  سند ضريبة
                </Button>
              )}
            </Stack>
          </Box>
        </Box>
      </Paper>

      {/* ملخص السندات */}
      {summary && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{
              p: 2,
              textAlign: 'center',
              background: 'linear-gradient(135deg, #66bb6a 0%, #43a047 100%)',
              color: 'white',
              borderRadius: 2,
              boxShadow: 3
            }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>إجمالي المقبوضات</Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {new Intl.NumberFormat('en-US', { minimumFractionDigits: 2 }).format(summary.totalReceipts || 0)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{
              p: 2,
              textAlign: 'center',
              background: 'linear-gradient(135deg, #ef5350 0%, #d32f2f 100%)',
              color: 'white',
              borderRadius: 2,
              boxShadow: 3
            }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>إجمالي المدفوعات</Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {new Intl.NumberFormat('en-US', { minimumFractionDigits: 2 }).format(summary.totalPayments || 0)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{
              p: 2,
              textAlign: 'center',
              background: 'linear-gradient(135deg, #42a5f5 0%, #1976d2 100%)',
              color: 'white',
              borderRadius: 2,
              boxShadow: 3
            }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>صافي التدفق النقدي</Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {new Intl.NumberFormat('en-US', { minimumFractionDigits: 2 }).format(summary.netCashFlow || 0)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{
              p: 2,
              textAlign: 'center',
              background: 'linear-gradient(135deg, #ffa726 0%, #fb8c00 100%)',
              color: 'white',
              borderRadius: 2,
              boxShadow: 3
            }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>الفترة</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {dayjs(filters.fromDate).format('YYYY/MM/DD')} - {dayjs(filters.toDate).format('YYYY/MM/DD')}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* فلاتر البحث */}
      <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 3 }}>
        <CardHeader
          title="فلاتر البحث"
          avatar={<FilterIcon />}
          action={
            <IconButton onClick={() => setFiltersOpen(!filtersOpen)}>
              {filtersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          }
          sx={{
            bgcolor: 'grey.100',
            '& .MuiCardHeader-title': {
              fontWeight: 'bold'
            }
          }}
        />
        <Collapse in={filtersOpen}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  select
                  label="نوع السند"
                  value={filters.voucherType}
                  onChange={(e) => handleFilterChange('voucherType', e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                >
                  {voucherTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  select
                  label="طريقة الدفع"
                  value={filters.paymentType}
                  onChange={(e) => handleFilterChange('paymentType', e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                >
                  {paymentTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  select
                  label="العميل"
                  value={filters.customerId}
                  onChange={(e) => handleFilterChange('customerId', e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                >
                  <MenuItem value="">جميع العملاء</MenuItem>
                  {customers.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  select
                  label="المورد"
                  value={filters.supplierId}
                  onChange={(e) => handleFilterChange('supplierId', e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                >
                  <MenuItem value="">جميع الموردين</MenuItem>
                  {suppliers.map((supplier) => (
                    <MenuItem key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  type="date"
                  label="من تاريخ"
                  value={filters.fromDate}
                  onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  type="date"
                  label="إلى تاريخ"
                  value={filters.toDate}
                  onChange={(e) => handleFilterChange('toDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="أقل مبلغ"
                  value={filters.minAmount}
                  onChange={(e) => handleFilterChange('minAmount', e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="أكبر مبلغ"
                  value={filters.maxAmount}
                  onChange={(e) => handleFilterChange('maxAmount', e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="البحث في النصوص"
                  value={filters.searchText}
                  onChange={(e) => handleFilterChange('searchText', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    )
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <Stack direction="row" spacing={2} justifyContent="center">
                  <Button
                    variant="contained"
                    startIcon={<SearchIcon />}
                    onClick={handleSearch}
                    sx={{ borderRadius: 2, px: 4 }}
                  >
                    بحث
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={handleReset}
                    sx={{ borderRadius: 2, px: 4 }}
                  >
                    إعادة تعيين
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Collapse>
      </Card>

      {/* جدول السندات */}
      <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
        <CardHeader
          title={`قائمة السندات (${vouchers.length})`}
          sx={{
            bgcolor: 'grey.100',
            '& .MuiCardHeader-title': {
              fontWeight: 'bold'
            }
          }}
        />
        <CardContent>
          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={vouchers}
              columns={columns}
              loading={loading}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 25 }
                }
              }}
              pageSizeOptions={[25, 50, 100]}
              disableRowSelectionOnClick
              slots={{ toolbar: GridToolbar }}
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 300 }
                }
              }}
              localeText={{
                noRowsLabel: 'لا توجد سجلات',
                toolbarExport: 'تصدير',
                toolbarColumns: 'الأعمدة',
                toolbarFilters: 'الفلاتر',
                toolbarQuickFilterPlaceholder: 'بحث سريع...',
              }}
              sx={{
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#f5f5f5',
                  fontWeight: 'bold'
                },
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid #e0e0e0'
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#f8f9fa'
                }
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* مربع حوار تأكيد الحذف */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>تأكيد الحذف</DialogTitle>
        <DialogContent>
          <Typography>هل أنت متأكد من حذف السند رقم {selectedVoucher?.voucherNumber}؟</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            لا يمكن التراجع عن هذا الإجراء.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>إلغاء</Button>
          <Button onClick={() => handleDeleteVoucher(selectedVoucher?.id)} color="error" variant="contained">
            حذف
          </Button>
        </DialogActions>
      </Dialog>

      {/* مربع حوار الطباعة */}
      <VoucherPrintDialog
        open={printDialogOpen}
        onClose={() => setPrintDialogOpen(false)}
        voucher={selectedVoucherForPrint}
        companyInfo={companyInfo}
      />
    </Container>
  );
};

export default VouchersListPage;